import { observer } from "mobx-react-lite/dist"
import { useProposalsEditServicesStore } from "./ProposalsEditServicesStore"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import ToggleButtonGroupCustom from "reusable/mui/toggle-button-group/ToggleButtonGroupCustom"
import { useTheme } from "theme/useTheme"
import useStore from "reusable/mobx-store-utils/useStore"
import { ProposalStep2PriceTypeEnum, ProposalStep2ServiceDto, proposalStep2PriceTypeEnum } from "@gap/backend/app-services/proposal/models/ProposalStep2Dto"
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd"
import LexicalRichTextEditor, { LexicalMode } from "reusable/rich-text-editors/lexical/LexicalRichTextEditor"
import MoreMenu from "reusable/more-menu/MoreMenu"
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DragHandleIcon from '@mui/icons-material/DragHandleOutlined'
import { useProposalsEditStore } from "../ProposalsEditStore"

interface ProposalServiceProps {
   service: ProposalStep2ServiceDto
   dragHandleProps: DraggableProvidedDragHandleProps | null | undefined
}

export default observer(function ProposalsEditServicesService({ service, dragHandleProps }: ProposalServiceProps) {
   const theme = useTheme()
   const proposalsEditServicesStore = useProposalsEditServicesStore()

   const proposalsEditStore = useProposalsEditStore()
   const serviceStore = proposalsEditStore.sp.serviceStore

   const localStore = useStore(() => ({
      priceType: service.servicePriceType,
      get moreMenuOptions() {
         return [{
            title: `Edit`,
            callback: localStore.onEditClick,
            icon: EditOutlinedIcon
         }, {
            title: `Reorder`,
            callback: localStore.onReorderClick,
            icon: DragHandleIcon,
            sx: { borderBottom: `1px solid ${theme.palette.gap.primary['200']}` }
         }, {
            title: `Delete`,
            callback: localStore.onDeleteClick,
            icon: DeleteOutlineIcon
         }]
      },
      onEditClick: () => {
         serviceStore.selectedServiceId = service.serviceId
         serviceStore.openServiceDrawer('edit')
      },
      onReorderClick: () => {
         proposalsEditServicesStore.isDragging = true
      },
      onDeleteClick: () => {
         proposalsEditServicesStore.deleteServiceById(service.serviceId)
      },
      onNewPriceType: (_event: React.MouseEvent<HTMLElement, MouseEvent>, newValue: ProposalStep2PriceTypeEnum) => {
         if (newValue) {
            localStore.priceType = newValue
            service.servicePriceType = newValue
            proposalsEditServicesStore.step = proposalsEditServicesStore.step.map(stepService => stepService.serviceId === service.serviceId ? service : stepService)
         }
      },
   }))

   return (
      <Box sx={{ border: `1px solid ${theme.palette.gap.primary['200']}`, borderRadius: '8px', p: theme.spacing(2) }}>
         <Box sx={{ display: 'flex' }}>
            <Box sx={{ flexGrow: 1 }}>
               <Typography variant="subtitle2">{service.serviceName}</Typography>
            </Box>

            <Box>
               <ToggleButtonGroupCustom
                  value={localStore.priceType}
                  onChange={localStore.onNewPriceType}
                  options={[...proposalStep2PriceTypeEnum]}
                  exclusive
                  size="small"
                  sx={{ margin: '0 auto' }}
                  buttonsxprops={{ textTransform: 'none', borderRadius: theme.spacing(1) }}
               />
            </Box>

            <Box>
               <Box sx={{ ...(proposalsEditServicesStore.isDragging ? { display: 'none' } : {}) }}>
                  <MoreMenu
                     icon={<MoreVertIcon className="MoreMenuActionClass" sx={{ mt: 1 }} />}
                     width="123px"
                     options={localStore.moreMenuOptions}
                  />
               </Box>
               <Box {...dragHandleProps} sx={{ ...(proposalsEditServicesStore.isDragging ? {} : { display: 'none' }) }}>
                  <DragHandleIcon fontSize="small" sx={{ ml: theme.spacing(1), mt: theme.spacing(1) }} />
               </Box>
            </Box>
         </Box>

         {service.serviceDefinition && <Typography variant="body1" sx={{ mt: theme.spacing(3) }}>{service.serviceDefinition}</Typography>}

         {service.serviceHowItWorks && <>
            <Typography variant="body2" sx={{ mt: theme.spacing(3), fontWeight: 'bold' }}>How it works</Typography>
            <LexicalRichTextEditor key={`how-it-works-${service.serviceId}`} value={service.serviceHowItWorks} mode={LexicalMode.View} disabled={true} />
         </>}
      </Box>
   );
})
